// SGNL-246 — Admin sub-panel for managing the Revenue:RecordType
// exclusion list that the BE applies to every revenue query.
//
// Lives under /admin → "Revenue Exclusions" sub-tab. Lets ops add a
// new pass-through RecordType the day a new SF category appears,
// without a Signal deploy. The BE seeded three known values
// (External Licenses/Fees, License/Commission, Non-Currency) at
// migration time; this surface lets operators add more or remove the
// seeded ones if Bounteous finance ever changes the policy.

import {
  ApiError,
  createRevenueExclusion,
  deleteRevenueExclusion,
  fetchRevenueExclusions,
} from "../api";
import { esc } from "../lib/format";
import type { RevenueExclusionRow } from "../types";

// Module-level state — tab-scoped, mirrors the pattern in
// revenue_calendar.ts. Reset between tab mounts via the exported
// reset function (called by vitest beforeEach).
interface ExclusionsState {
  rows: RevenueExclusionRow[];
  loading: boolean;
  error: string | null;
  // Form state — separate from the row list so a failed POST doesn't
  // wipe what the operator was typing.
  formCategory: string;
  formReason: string;
  formSubmitting: boolean;
  formError: string | null;
}

const state: ExclusionsState = {
  rows: [],
  loading: false,
  error: null,
  formCategory: "",
  formReason: "",
  formSubmitting: false,
  formError: null,
};

export function _resetRevenueExclusionsState(): void {
  state.rows = [];
  state.loading = false;
  state.error = null;
  state.formCategory = "";
  state.formReason = "";
  state.formSubmitting = false;
  state.formError = null;
}

export async function renderRevenueExclusionsPanel(host: HTMLElement): Promise<void> {
  // Shell — sub-region IDs let in-place re-renders avoid blinking the
  // form fields the operator might be typing into.
  host.innerHTML = `
    <section class="admin-exclusions" data-testid="admin-exclusions">
      <header class="admin-exclusions__header">
        <h3 class="admin-exclusions__title">Revenue: Record Type — exclusion list</h3>
        <p class="admin-exclusions__subtitle">
          Pass-through revenue line items whose totals are filtered out of every
          revenue query (ITB, Qualified, Unqualified, Revenue Visibility, Pipeline
          Pacing). Adding a row takes effect on the next request — no deploy
          needed. See <code>docs/sgnl-244-itb-reconciliation.md</code> for why
          this filter exists.
        </p>
      </header>
      <div class="admin-exclusions__form-host" data-testid="admin-exclusions-form"></div>
      <div class="admin-exclusions__list-host" data-testid="admin-exclusions-list"></div>
    </section>
  `;
  _bindForm(host);
  await _refreshList(host);
}

function _bindForm(host: HTMLElement): void {
  const slot = host.querySelector<HTMLElement>("[data-testid='admin-exclusions-form']");
  if (!slot) return;
  _renderForm(slot);
  slot.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void _submitForm(host);
  });
  slot.addEventListener("input", (ev) => {
    const target = ev.target as HTMLInputElement | HTMLTextAreaElement;
    if (target.name === "category") state.formCategory = target.value;
    else if (target.name === "reason") state.formReason = target.value;
  });
}

function _renderForm(slot: HTMLElement): void {
  // Note: not re-binding on each call — submit handler delegated on
  // the slot itself so the inner form can be replaced freely.
  slot.innerHTML = `
    <form class="admin-exclusions__form" data-testid="admin-exclusions-form-el">
      <label class="admin-exclusions__field">
        <span class="admin-exclusions__label">Revenue Record Type</span>
        <input
          type="text"
          name="category"
          required
          maxlength="64"
          placeholder="e.g. External Licenses/Fees"
          value="${esc(state.formCategory)}"
          data-testid="admin-exclusions-category-input"
        />
      </label>
      <label class="admin-exclusions__field">
        <span class="admin-exclusions__label">Reason (optional)</span>
        <input
          type="text"
          name="reason"
          maxlength="256"
          placeholder="Pass-through / non-cash / etc."
          value="${esc(state.formReason)}"
          data-testid="admin-exclusions-reason-input"
        />
      </label>
      <button
        type="submit"
        class="admin-exclusions__submit"
        data-testid="admin-exclusions-submit"
        ${state.formSubmitting ? "disabled" : ""}
      >${state.formSubmitting ? "Adding…" : "Add exclusion"}</button>
      ${
        state.formError
          ? `<div class="admin-exclusions__form-error"
                  data-testid="admin-exclusions-form-error">${esc(state.formError)}</div>`
          : ""
      }
    </form>
  `;
}

async function _submitForm(host: HTMLElement): Promise<void> {
  const slot = host.querySelector<HTMLElement>("[data-testid='admin-exclusions-form']");
  if (!slot) return;
  const cat = state.formCategory.trim();
  if (cat.length === 0) {
    state.formError = "Category is required.";
    _renderForm(slot);
    return;
  }
  state.formSubmitting = true;
  state.formError = null;
  _renderForm(slot);
  try {
    await createRevenueExclusion(cat, state.formReason.trim() || null);
    // Success — clear the form fields and refresh the list so the
    // new row appears (or the existing row's `added_by` confirms the
    // idempotent POST hit the existing entry).
    state.formCategory = "";
    state.formReason = "";
    await _refreshList(host);
  } catch (err) {
    state.formError =
      err instanceof ApiError
        ? err.status === 422
          ? "Invalid category — only letters, numbers, and basic punctuation allowed."
          : err.status === 403
            ? "Admin permission required."
            : `Add failed: HTTP ${err.status}`
        : String(err);
  } finally {
    state.formSubmitting = false;
    _renderForm(slot);
  }
}

async function _refreshList(host: HTMLElement): Promise<void> {
  const slot = host.querySelector<HTMLElement>("[data-testid='admin-exclusions-list']");
  if (!slot) return;
  state.loading = true;
  state.error = null;
  _renderList(slot);
  try {
    const resp = await fetchRevenueExclusions();
    state.rows = resp.exclusions;
  } catch (err) {
    state.error =
      err instanceof ApiError
        ? err.status === 403
          ? "Admin permission required to view exclusions."
          : `Load failed: HTTP ${err.status}`
        : String(err);
  } finally {
    state.loading = false;
    _renderList(slot);
  }
}

function _renderList(slot: HTMLElement): void {
  if (state.loading && state.rows.length === 0) {
    slot.innerHTML = `
      <div class="admin-exclusions__empty" data-testid="admin-exclusions-loading">
        Loading exclusions…
      </div>`;
    return;
  }
  if (state.error) {
    slot.innerHTML = `
      <div class="admin-exclusions__error" data-testid="admin-exclusions-list-error">
        ${esc(state.error)}
      </div>`;
    return;
  }
  if (state.rows.length === 0) {
    slot.innerHTML = `
      <div class="admin-exclusions__empty" data-testid="admin-exclusions-empty">
        No exclusions configured. Revenue totals will include every Record Type.
      </div>`;
    return;
  }
  slot.innerHTML = `
    <table class="admin-exclusions__table" data-testid="admin-exclusions-table">
      <thead>
        <tr>
          <th>Category</th>
          <th>Reason</th>
          <th>Added by</th>
          <th>Added at</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        ${state.rows.map(_rowHTML).join("")}
      </tbody>
    </table>
  `;
  // Bind delete buttons after the table is in the DOM.
  slot.querySelectorAll<HTMLButtonElement>("[data-testid^='admin-exclusions-delete-']")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const cat = btn.dataset.category;
        if (!cat) return;
        const host = slot.closest<HTMLElement>("[data-testid='admin-exclusions']");
        if (host) void _deleteRow(host, cat);
      });
    });
}

function _rowHTML(row: RevenueExclusionRow): string {
  // System-seeded rows have added_by=null. Show "system" instead of
  // an empty cell so the operator knows which rows came pre-populated.
  const addedBy = row.added_by ?? "system";
  // SAFETY: testids include the category — encode for the attribute
  // selector but the displayed value still goes through esc().
  const testidSlug = encodeURIComponent(row.category);
  return `
    <tr data-testid="admin-exclusions-row-${esc(testidSlug)}">
      <td><code>${esc(row.category)}</code></td>
      <td>${esc(row.reason ?? "—")}</td>
      <td>${esc(addedBy)}</td>
      <td>${esc(row.added_at.slice(0, 10))}</td>
      <td>
        <button type="button"
                class="admin-exclusions__delete"
                data-testid="admin-exclusions-delete-${esc(testidSlug)}"
                data-category="${esc(row.category)}"
                aria-label="Remove ${esc(row.category)} from exclusions"
        >Remove</button>
      </td>
    </tr>
  `;
}

async function _deleteRow(host: HTMLElement, category: string): Promise<void> {
  // No confirmation modal v0 — same pattern as the existing admin
  // surfaces. The operation is reversible (just POST the category
  // back to re-add it).
  try {
    await deleteRevenueExclusion(category);
    await _refreshList(host);
  } catch (err) {
    const msg =
      err instanceof ApiError
        ? err.status === 403
          ? "Admin permission required."
          : err.status === 404
            ? "Already deleted."
            : `Delete failed: HTTP ${err.status}`
        : String(err);
    state.error = msg;
    const slot = host.querySelector<HTMLElement>(
      "[data-testid='admin-exclusions-list']",
    );
    if (slot) _renderList(slot);
  }
}

// Test seam — vitest tests assert on internal state via this seam
// rather than DOM walks alone (cheaper assertions when validating
// the form submit happy path / idempotent POST behaviour).
export function _getRevenueExclusionsState(): Readonly<ExclusionsState> {
  return state;
}
